import React, { useEffect } from 'react';
import { Image, Form } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import useCommonStore, { setModalConfig } from 'store/common';
import { logStoryViewed, useGetStoriesQuery } from 'services';
import { S3_SMALL_THUMB, S3_URL } from 'constants';
import toast from 'react-hot-toast';
import { convertJpegToPng, getFileName, getFileType } from 'utils';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';
import { ALLOWEDVIDEOFORMATS } from 'constants';

const TopStories: React.FC = () => {
	const { translate } = useTranslate();
	const { data: stories, refetch } = useGetStoriesQuery();
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);

	useEffect(() => {
		if (modalConfig?.visible === false) {
			refetch();
		}
	}, [modalConfig?.visible]);

	const handleShow = (index: number, storyId: any) => {
		logStoryViewed(storyId);
		setModalConfig({
			visible: true,
			data: stories,
			id: index,
			type: 'socialStoryPreview',
			onClose: () => {
				hideCommonModal();
				refetch();
			},
			props: {
				fullscreen: true,
				centered: true,
				className: 'story-preview-modal',
			},
		});
	};

	const onAssetsChange = async (e: any) => {
		const files = Array.from(e.target.files) as File[];
		const maxFileSize = 10 * 1024 * 1024;
		const allowedVideoFormatsString = ALLOWEDVIDEOFORMATS.map(
			(format: any) => format.split('/')[1],
		).join(', ');

		if (files.length === 0) return; // Early exit if no files are selected

		if (files.length > 10) {
			return toast.error(translate('Validation.postMaxStories', { number: '10' }));
		}

		const fileSizeExceed = files.some((file: any) => file.size > maxFileSize); // Check if any file exceeds max size
		if (fileSizeExceed) {
			return toast.error(translate('Validation.fileSizeShouldBeLessThan', { size: '10 MB' }));
		}

		let invalidVideoFormat = false;
		for (const file of files) {
			if (getFileType(file?.type) === 'video') {
				// Check if the video format is valid
				if (!ALLOWEDVIDEOFORMATS.includes(file.type)) {
					invalidVideoFormat = true;
				}
			}
		}

		if (invalidVideoFormat) {
			return toast.error(
				translate('ToastMessage.invalidVideoFormat', { formats: allowedVideoFormatsString }),
			);
		}

		const convertedFilesPromises = files.map(async (file: any) => {
			if (file.type.startsWith('image/') && file.type !== 'image/png') {
				const pngBlob = await convertJpegToPng(file); // Convert to PNG
				const pngFile = new File([pngBlob], file.name.replace(/\.[^/.]+$/, '.png'), {
					type: 'image/png',
				});
				return pngFile;
			}
			return file; // If not a JPEG, keep the original file
		});

		const convertedFiles = await Promise.all(convertedFilesPromises);

		// Proceed if all checks pass
		setModalConfig({
			visible: true,
			data: convertedFiles,
			type: 'addStory',
			props: {
				fullscreen: true,
				centered: true,
				dialogClassName: 'dialog-assets-story-carousel',
			},
		});
	};

	return (
		<div className="top-stories white-box bg-white">
			<h5 className="fw-bold mb-2">{translate('Social.topStories')}</h5>
			<div className="top-stories-list d-flex gap-2 gap-md-3">
				<div className="story-box add-story position-relative">
					<Form.Control
						className="border-0"
						type="file"
						multiple
						onChange={onAssetsChange}
						accept="image/*,video/*"
					/>
					<div className="position-absolute top-50 start-50 translate-middle text-center w-100">
						<i className="ri-add-circle-fill"></i>
						<h6 className="fw-bold mb-0">{translate('Social.addStory')}</h6>
					</div>
				</div>
				{stories?.length > 0 &&
					stories?.map((story: any, index: number) => {
						return (
							<div key={index}>
								<div
									className="story-box position-relative"
									onClick={() => handleShow(index, story?.userId)}
								>
									<Image
										src={
											story?.story[0]?.mediaType === 'video'
												? S3_SMALL_THUMB + getFileName(story?.story[0]?.mediaItem)
												: S3_URL + story?.story[0]?.mediaItem
										}
										className="h-100 w-100 object-fit-cover"
										loading="lazy"
									/>
									<div className="story-user-info position-absolute bottom-0 start-0 w-100 d-flex gap-1 gap-md-2">
										<div className="story-user-info-img rounded-circle">
											<Image
												src={
													story?.profilePicture
														? S3_URL + story.profilePicture
														: IMAGE_PATHS.UserDefault
												}
												className="h-100 w-100 object-fit-cover rounded-circle"
												loading="lazy"
											/>
										</div>
										<p className="text-white mb-0 flex-1">{story?.fullName}</p>
									</div>
								</div>
							</div>
						);
					})}
			</div>
		</div>
	);
};

export default TopStories;
